import { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Trophy, Home, Settings, ExternalLink } from 'lucide-react';

interface LayoutProps {
  children: ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const location = useLocation();
  
  const navigation = [
    { name: 'Pick Games', href: '/', icon: Home, current: location.pathname === '/' },
    { name: 'Leaderboard', href: '/leaderboard', icon: Trophy, current: location.pathname === '/leaderboard' },
    { name: 'Admin', href: '/admin', icon: Settings, current: location.pathname === '/admin' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* $Nuts Branding Bar */}
      <div className="bg-gradient-to-r from-orange-500 to-yellow-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-2">
            <div className="flex items-center">
              <img 
                src="/nuts-logo.jpg" 
                alt="$NUTS Logo" 
                className="h-8 w-auto mr-3"
                onError={(e) => {
                  // Fallback to text if image not found
                  e.currentTarget.style.display = 'none';
                  const fallbackElement = e.currentTarget.nextElementSibling as HTMLElement;
                  if (fallbackElement) {
                    fallbackElement.style.display = 'block';
                  }
                }}
              />
              <div className="text-2xl font-bold" style={{ display: 'none' }}>$NUTS</div>
              <div className="text-xl font-bold">$NUTS</div>
              <div className="ml-4 text-sm font-medium">Sports Pick'em Contests</div>
            </div>
            <a
              href="https://xrpsquirrel.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-white/20 hover:bg-white/30 transition-colors"
            >
              <ExternalLink className="h-4 w-4 mr-1" />
              $Nuts Home Page
            </a>
          </div>
        </div>
      </div>
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="flex items-center">
                  <Trophy className="h-8 w-8 text-indigo-600" />
                  <span className="ml-2 text-xl font-bold text-gray-900">NBA Pick'em</span>
                </div>
              </div>
            </div>
            
            <nav className="flex space-x-8">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`
                      inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors
                      ${item.current
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                      }
                    `}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-gray-500 text-sm">
            <p>NBA Pick'em Contest • Powered by XRPL & $NUTS</p>
            <p className="mt-1">Entry Fee: 1000 $NUTS • Payouts: 1st, 2nd, and 3rd Place</p>
            <p className="mt-3 text-xs border-t pt-3 max-w-4xl mx-auto">
              <strong>DISCLAIMER:</strong> This is a skill-based entertainment contest for fun and educational purposes only. 
              Participants use knowledge and skill to predict game outcomes. This contest is not gambling or a game of chance. 
              Must be 18+ to participate. Void where prohibited by law. Play responsibly.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
